import OpenAI from 'openai';

/**
 * OpenAI Voice Service
 * Provides text-to-speech and speech-to-text functionality using OpenAI APIs
 */

const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true // Required for client-side usage
});

// Audio playback management
let currentAudio = null;
let audioQueue = [];
let currentResolver = null; // Store Promise resolver to allow cancellation

/**
 * Text-to-Speech using OpenAI
 * @param {string} text - Text to convert to speech
 * @param {string} voice - Voice to use (alloy, echo, fable, onyx, nova, shimmer)
 * @returns {Promise<void>}
 */
export async function textToSpeech(text, voice = 'alloy') {
  try {
    console.log('[OpenAI TTS] Generating speech for:', text.substring(0, 50) + '...');

    // Cancel any currently playing audio
    if (currentAudio) {
      currentAudio.pause();
      currentAudio = null;
    }

    // Call OpenAI TTS API
    const response = await openai.audio.speech.create({
      model: 'tts-1',
      voice: voice,
      input: text,
    });

    // Convert response to audio blob
    const audioBlob = await response.blob();
    const audioUrl = URL.createObjectURL(audioBlob);

    // Create and play audio element
    currentAudio = new Audio(audioUrl);

    return new Promise((resolve, reject) => {
      currentResolver = resolve; // Store resolver for cancellation

      currentAudio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudio = null;
        currentResolver = null;
        console.log('[OpenAI TTS] Playback completed');
        resolve();
      };

      currentAudio.onpause = () => {
        URL.revokeObjectURL(audioUrl);
        currentResolver = null;
        console.log('[OpenAI TTS] Playback paused/cancelled');
        resolve(); // Resolve when paused to unblock awaiting code
      };

      currentAudio.onerror = (error) => {
        console.error('[OpenAI TTS] Playback error:', error);
        currentAudio = null;
        currentResolver = null;
        reject(error);
      };

      currentAudio.play().catch((error) => {
        console.error('[OpenAI TTS] Failed to play audio:', error);
        currentResolver = null;
        reject(error);
      });
    });

  } catch (error) {
    console.error('[OpenAI TTS] Error:', error);
    throw error;
  }
}

/**
 * Cancel any ongoing TTS playback
 */
export function cancelTTS() {
  if (currentAudio) {
    currentAudio.pause(); // Triggers onpause handler which resolves Promise
    currentAudio = null;
  }
  // Ensure Promise is resolved even if audio is already null
  if (currentResolver) {
    currentResolver();
    currentResolver = null;
  }
}

/**
 * Check if OpenAI API is available
 * @returns {boolean}
 */
export function isOpenAIAvailable() {
  return !!process.env.REACT_APP_OPENAI_API_KEY;
}

// Speech recognition state
let mediaRecorder = null;
let audioChunks = [];
let isListening = false;
let recognitionCallback = null;
let silenceTimer = null;
let audioStream = null;
const CHUNK_DURATION = 3000; // Send audio every 3 seconds
const SILENCE_DURATION = 2000; // Stop after 2 seconds of silence

/**
 * Start continuous listening for speech
 * @param {Function} onTranscription - Callback when text is transcribed (text) => void
 * @param {Function} onError - Callback for errors (error) => void
 * @returns {Promise<void>}
 */
export async function startListening(onTranscription, onError) {
  if (isListening) {
    console.warn('[OpenAI Transcription] Already listening');
    return;
  }

  try {
    console.log('[OpenAI Transcription] Starting continuous listening...');

    // Request microphone access
    audioStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: 16000,
        echoCancellation: true,
        noiseSuppression: true,
      }
    });

    // Create MediaRecorder
    mediaRecorder = new MediaRecorder(audioStream, {
      mimeType: 'audio/webm;codecs=opus'
    });

    recognitionCallback = onTranscription;
    audioChunks = [];
    isListening = true;

    // Handle audio data
    mediaRecorder.ondataavailable = async (event) => {
      if (event.data.size > 0) {
        audioChunks.push(event.data);

        // Process chunks periodically
        if (audioChunks.length > 0) {
          await processAudioChunks();
        }
      }
    };

    mediaRecorder.onerror = (event) => {
      console.error('[OpenAI Transcription] MediaRecorder error:', event.error);
      if (onError) onError(event.error);
    };

    mediaRecorder.onstop = () => {
      console.log('[OpenAI Transcription] Recording stopped');
      isListening = false;
    };

    // Start recording with time slices
    mediaRecorder.start(CHUNK_DURATION);

    console.log('[OpenAI Transcription] Listening started');

  } catch (error) {
    console.error('[OpenAI Transcription] Failed to start listening:', error);
    isListening = false;
    if (onError) onError(error);
    throw error;
  }
}

/**
 * Stop listening for speech
 */
export function stopListening() {
  console.log('[OpenAI Transcription] Stopping listening...');

  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
  }

  if (audioStream) {
    audioStream.getTracks().forEach(track => track.stop());
    audioStream = null;
  }

  if (silenceTimer) {
    clearTimeout(silenceTimer);
    silenceTimer = null;
  }

  isListening = false;
  audioChunks = [];
  recognitionCallback = null;
}

/**
 * Check if currently listening
 * @returns {boolean}
 */
export function isCurrentlyListening() {
  return isListening;
}

/**
 * Process accumulated audio chunks and send to OpenAI
 */
async function processAudioChunks() {
  if (audioChunks.length === 0) return;

  try {
    // Combine chunks into a single blob
    const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
    audioChunks = []; // Clear chunks after processing

    // Skip if audio is too small (likely silence)
    if (audioBlob.size < 1000) {
      return;
    }

    console.log('[OpenAI Transcription] Processing audio chunk:', audioBlob.size, 'bytes');

    // Convert to File object (required by OpenAI API)
    const audioFile = new File([audioBlob], 'audio.webm', { type: 'audio/webm' });

    // Send to OpenAI Whisper
    const transcription = await openai.audio.transcriptions.create({
      file: audioFile,
      model: 'whisper-1',
      language: 'en', // Can be made configurable
      response_format: 'text'
    });

    const text = transcription.trim();

    if (text && text.length > 0) {
      console.log('[OpenAI Transcription] Transcribed:', text);

      if (recognitionCallback) {
        recognitionCallback(text);
      }
    }

  } catch (error) {
    console.error('[OpenAI Transcription] Error processing audio:', error);
    // Don't stop listening on transcription errors - just log and continue
  }
}

/**
 * Match transcribed text against expected phrases
 * @param {string} transcribedText - Text from speech recognition
 * @param {Array<string>} expectedPhrases - List of phrases to match
 * @param {number} threshold - Similarity threshold (0-1)
 * @returns {Object|null} - { phrase, confidence } or null
 */
export function matchPhrase(transcribedText, expectedPhrases, threshold = 0.7) {
  if (!transcribedText || !expectedPhrases || expectedPhrases.length === 0) {
    return null;
  }

  const normalizedText = normalizeText(transcribedText);

  for (const phrase of expectedPhrases) {
    const normalizedPhrase = normalizeText(phrase);
    const similarity = calculateSimilarity(normalizedText, normalizedPhrase);

    console.log('[Phrase Matching]', {
      transcribed: transcribedText,
      expected: phrase,
      similarity: similarity.toFixed(2)
    });

    if (similarity >= threshold) {
      return {
        phrase: phrase,
        confidence: similarity,
        matched: transcribedText
      };
    }
  }

  return null;
}

/**
 * Normalize text for comparison
 * @param {string} text
 * @returns {string}
 */
function normalizeText(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, '') // Remove punctuation
    .replace(/\s+/g, ' ')    // Normalize whitespace
    .trim();
}

/**
 * Calculate similarity between two strings using Levenshtein distance
 * @param {string} str1
 * @param {string} str2
 * @returns {number} - Similarity score (0-1)
 */
function calculateSimilarity(str1, str2) {
  // Quick exact match check
  if (str1 === str2) return 1.0;

  // Check if one string contains the other
  if (str1.includes(str2) || str2.includes(str1)) {
    return Math.min(str1.length, str2.length) / Math.max(str1.length, str2.length);
  }

  // Levenshtein distance calculation
  const matrix = [];
  const len1 = str1.length;
  const len2 = str2.length;

  // Initialize matrix
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Calculate distances
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  const distance = matrix[len1][len2];
  const maxLength = Math.max(len1, len2);

  return 1 - (distance / maxLength);
}

export default {
  textToSpeech,
  cancelTTS,
  isOpenAIAvailable,
  startListening,
  stopListening,
  isCurrentlyListening,
  matchPhrase
};
